import {
  bugReportsApi,
  type BugReport,
  type BugReportFilters,
  type BugReportPaginatedResponse,
  type CreateBugReportRequest,
  type UpdateBugReportRequest,
} from '@/api/bug-reports'
import { ScreenshotService } from './screenshotService'

// Réexporter les types pour les stores
export type { BugReport, BugReportFilters, BugReportPaginatedResponse }

export interface CreateBugReportData {
  title: string
  description: string
  url: string
  priority?: 'low' | 'medium' | 'high' | 'critical'
  captureFullPage?: boolean
}

/**
 * Service pour gérer les remontées de bugs
 */
export class BugReportService {
  /**
   * Créer une remontée avec capture automatique du screenshot
   */
  static async createBugReport(data: CreateBugReportData): Promise<BugReport> {
    console.log('[BdC-BugReportService] Création remontée:', data.title)

    // Capturer le screenshot (utilise getDisplayMedia par défaut)
    const screenshot = data.captureFullPage
      ? await ScreenshotService.captureFullPage({ format: 'jpeg', quality: 0.8 })
      : await ScreenshotService.capture({ format: 'jpeg', quality: 0.8 })

    // Compresser si nécessaire (max 2MB)
    const compressedBlob = await ScreenshotService.compressImage(screenshot.blob, 2 * 1024 * 1024)

    // Convertir en base64
    const base64 = await ScreenshotService.blobToBase64(compressedBlob)

    // Récupérer les infos navigateur
    const browserInfo = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
    }

    const request: CreateBugReportRequest = {
      title: data.title,
      description: data.description,
      screenshot: base64,
      screenshotMimeType: screenshot.mimeType,
      url: data.url,
      browserInfo,
      priority: data.priority || 'medium',
    }

    const bugReport = await bugReportsApi.create(request)
    console.log('[BdC-BugReportService] Remontée créée:', bugReport.id)

    return bugReport
  }

  /**
   * Récupérer toutes les remontées avec filtres
   */
  static async getBugReports(filters?: BugReportFilters): Promise<BugReportPaginatedResponse> {
    console.log('[BdC-BugReportService] Récupération remontées avec filtres:', filters)
    return bugReportsApi.list(filters)
  }

  /**
   * Récupérer une remontée par ID
   */
  static async getBugReportById(id: number): Promise<BugReport> {
    console.log('[BdC-BugReportService] Récupération remontée:', id)
    return bugReportsApi.getById(id)
  }

  /**
   * Récupérer le screenshot d'une remontée (retourne null si 404)
   */
  static async getScreenshot(id: number): Promise<string | null> {
    console.log('[BdC-BugReportService] Récupération screenshot:', id)
    return bugReportsApi.getScreenshot(id)
  }

  /**
   * Mettre à jour une remontée
   */
  static async updateBugReport(id: number, data: UpdateBugReportRequest): Promise<BugReport> {
    console.log('[BdC-BugReportService] Mise à jour remontée:', id, data)
    return bugReportsApi.update(id, data)
  }

  /**
   * Supprimer une remontée
   */
  static async deleteBugReport(id: number): Promise<void> {
    console.log('[BdC-BugReportService] Suppression remontée:', id)
    return bugReportsApi.delete(id)
  }

  /**
   * Récupérer les statistiques
   */
  static async getStats() {
    console.log('[BdC-BugReportService] Récupération statistiques')
    return bugReportsApi.getStats()
  }
}

export const bugReportService = new BugReportService()
